import { RewardExchange } from "../types/karma-exchange.entity";

export type ExchangeStatus = NonNullable<RewardExchange["status"]>;
export type ExchangeCategory = NonNullable<RewardExchange["category"]>;

type ExchangeLike = {
  providerName?: string | null;
  status?: string | null;
  category?: string | null;
  reward?: { category?: string | null } | null;
};

export const defaultExchangeStatus: ExchangeStatus = "PENDING";

export const categoryLabelMap: Record<ExchangeCategory, string> = {
  PHISICAL: "Physical Reward",
  VOUCHER: "Voucher",
  DIGITAL: "Digital Reward",
};

export const statusUiConfig: Record<
  ExchangeStatus,
  {
    label: string;
    badgeClasses: string;
    panelClasses: string;
    hint: string;
  }
> = {
  PENDING: {
    label: "Pending",
    badgeClasses: "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
    panelClasses: "border-amber-200 bg-amber-50",
    hint: "Waiting for confirmation and fulfillment.",
  },
  PROCESSING: {
    label: "Processing",
    badgeClasses: "bg-sky-100 text-sky-800 ring-1 ring-sky-200",
    panelClasses: "border-sky-200 bg-sky-50",
    hint: "Exchange is approved and in progress.",
  },
  SHIPPED: {
    label: "Shipped",
    badgeClasses: "bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200",
    panelClasses: "border-indigo-200 bg-indigo-50",
    hint: "Reward has been shipped and is in transit.",
  },
  DELIVERED: {
    label: "Delivered",
    badgeClasses: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
    panelClasses: "border-emerald-200 bg-emerald-50",
    hint: "Reward reached the user successfully.",
  },
  REJECTED: {
    label: "Rejected",
    badgeClasses: "bg-rose-100 text-rose-800 ring-1 ring-rose-200",
    panelClasses: "border-rose-200 bg-rose-50",
    hint: "Exchange was rejected and will not be fulfilled.",
  },
};

export const getExchangeProviderName = (exchange: ExchangeLike): string =>
  exchange.providerName || "-";

export const getRewardCategory = (exchange: ExchangeLike): string | undefined =>
  exchange.reward?.category || exchange.category || undefined;

export const isPhysicalExchange = (exchange: ExchangeLike): boolean =>
  getRewardCategory(exchange) === "PHISICAL";

export const isTremendousDigitalExchange = (exchange: ExchangeLike): boolean =>
  getExchangeProviderName(exchange) === "TREMENDOUS" &&
  getRewardCategory(exchange) === "DIGITAL";

export const isTremendousPendingApproval = (exchange: ExchangeLike): boolean =>
  isTremendousDigitalExchange(exchange) &&
  (exchange.status || defaultExchangeStatus) === "PENDING";

export const getCategoryLabel = (category?: string | null): string => {
  if (!category) return "-";
  return categoryLabelMap[category as ExchangeCategory] || category;
};

export const getStatusPresentation = (
  status: ExchangeStatus,
  options?: { isTremendousPendingApproval?: boolean }
) => {
  if (options?.isTremendousPendingApproval) {
    return {
      label: "Pending Approval",
      badgeClasses: statusUiConfig.PENDING.badgeClasses,
      panelClasses: statusUiConfig.PENDING.panelClasses,
    };
  }

  const config = statusUiConfig[status];
  return {
    label: config.label,
    badgeClasses: config.badgeClasses,
    panelClasses: config.panelClasses,
  };
};

export const getStatusHint = (
  status: ExchangeStatus,
  opts: { isPhysicalExchange: boolean; isTremendousDigitalExchange: boolean }
): string => {
  if (opts.isTremendousDigitalExchange) {
    if (status === "PENDING")
      return "Waiting for admin approval before creating the Tremendous order.";
    if (status === "PROCESSING")
      return "Admin approved the request. Waiting for Tremendous webhook to mark delivery.";
    if (status === "DELIVERED")
      return "Tremendous confirmed the reward is ready to claim.";
    if (status === "REJECTED")
      return "Request was rejected before Tremendous order creation.";
  }

  if (opts.isPhysicalExchange) {
    if (status === "PENDING")
      return "Waiting for manual review before fulfillment starts.";
    if (status === "PROCESSING")
      return "Exchange is approved and being prepared for shipment.";
    if (status === "SHIPPED") return "Reward is on the way to the user.";
    if (status === "DELIVERED")
      return "Reward reached the user successfully.";
    if (status === "REJECTED")
      return "Exchange was rejected and will not be fulfilled.";
  }

  return statusUiConfig[status].hint;
};

export const getStatusStepText = (
  status: ExchangeStatus,
  opts: { isTremendousDigitalExchange: boolean }
): string => {
  if (status === "PENDING") {
    return opts.isTremendousDigitalExchange
      ? "Waiting for admin approval"
      : "Awaiting review";
  }
  if (status === "PROCESSING") {
    return opts.isTremendousDigitalExchange
      ? "Waiting for Tremendous webhook confirmation"
      : "Ready for delivery completion";
  }
  if (status === "SHIPPED") return "In transit";
  return "Final status";
};
